import React from 'react';
import { Link } from 'react-router-dom';
import { Minus, TrendingDown, TrendingUp } from 'lucide-react';
import { Avatar, Pill } from '../../../components/ui';
import type { SignalSeverity, SubjectRef } from '../../../types/monitoring';
import { SENTIMENT_LABEL, SEVERITY_LABEL, sentimentBand, severityTone, subjectHref } from '../format';

/** Severity reads from the word first; the tone only reinforces it */
export const SeverityPill: React.FC<{ severity: SignalSeverity; className?: string }> = ({ severity, className }) => (
  <Pill tone={severityTone(severity)} className={className}>
    {SEVERITY_LABEL[severity]}
  </Pill>
);

/**
 * Sentiment as a shape and a word, never colour alone: an arrow down/level/up
 * with "Negative" / "Neutral" / "Positive", and the raw score in the tooltip.
 */
export const SentimentMark: React.FC<{ value: number; withLabel?: boolean; className?: string }> = ({
  value,
  withLabel = true,
  className = '',
}) => {
  const band = sentimentBand(value);
  const Icon = band === 'negative' ? TrendingDown : band === 'positive' ? TrendingUp : Minus;
  const tone = band === 'negative' ? 'text-primary-600' : band === 'positive' ? 'text-emerald-600' : 'text-neutral-400';
  const score = value > 0 ? `+${value.toFixed(2)}` : value.toFixed(2);
  return (
    <span
      className={`inline-flex items-center gap-1 whitespace-nowrap text-[12px] text-neutral-500 ${className}`}
      title={`Sentiment ${score}`}
    >
      <Icon className={`h-3.5 w-3.5 ${tone}`} strokeWidth={1.75} />
      {withLabel && SENTIMENT_LABEL[band]}
      <span className="sr-only"> (score {score})</span>
    </span>
  );
};

/** The entity a signal or alert is about, linked to its profile */
export const SubjectLink: React.FC<{ subject: SubjectRef; className?: string; withAvatar?: boolean }> = ({
  subject,
  className = '',
  withAvatar = true,
}) => (
  <Link
    to={subjectHref(subject)}
    className={`inline-flex min-w-0 items-center gap-1.5 text-[13px] text-ink hover:underline hover:decoration-neutral-300 hover:underline-offset-2 ${className}`}
  >
    {withAvatar && <Avatar name={subject.label} kind={subject.type} size="xs" />}
    <span className="truncate">{subject.label}</span>
  </Link>
);

/** A dot-separated meta line that never leaves a dangling separator */
export const MetaLine: React.FC<{ parts: Array<React.ReactNode>; className?: string }> = ({ parts, className = '' }) => {
  const shown = parts.filter((part) => part !== null && part !== undefined && part !== '' && part !== false);
  return (
    <span className={`inline-flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-[12px] text-neutral-400 ${className}`}>
      {shown.map((part, index) => (
        <React.Fragment key={index}>
          {index > 0 && <span aria-hidden="true">·</span>}
          <span className="min-w-0 truncate">{part}</span>
        </React.Fragment>
      ))}
    </span>
  );
};

/**
 * One honest line about what is wrong and what to do — never a spinner that
 * goes nowhere. `tone="quiet"` for "nothing here yet", loud for real failures.
 */
export const Notice: React.FC<{
  children: React.ReactNode;
  tone?: 'quiet' | 'error';
  action?: React.ReactNode;
  className?: string;
}> = ({ children, tone = 'error', action, className = '' }) => (
  <div
    role={tone === 'error' ? 'alert' : 'status'}
    className={`flex flex-wrap items-center gap-x-3 gap-y-1 rounded-xl px-3.5 py-2.5 text-[13px] ${
      tone === 'error' ? 'bg-primary-50 text-primary-700' : 'bg-neutral-50 text-neutral-600'
    } ${className}`}
  >
    <span className="min-w-0 flex-1">{children}</span>
    {action}
  </div>
);
